/**
 * Dashboard.jsx — Phase 20A — Management Cockpit
 *
 * Single-page decision-oriented overview for school admins / principals.
 * Powered by GET /api/school/dashboard/cockpit (single round-trip).
 *
 * Sections:
 *  - Header (school + active session + onboarding shortcuts)
 *  - KPI trio: Today / Month-to-date / Session-to-date
 *  - 30-day collection trend chart
 *  - Alerts strip (computed server-side; max 4)
 *  - Top pending classes + Today's collectors + Mode mix donut
 *
 * Every KPI / alert / list item is clickable → carries context to the
 * relevant Reports tab.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  ChalkboardTeacher,
  CurrencyInr,
  Calendar,
  ArrowRight,
  Plus,
  TrendUp,
  TrendDown,
  Hourglass,
  Receipt,
  Warning,
  Info,
  Lightning,
  CheckCircle,
  Bank,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// ---------- formatters ----------
const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const fmtShortINR = (n) => {
  const v = Number(n) || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}k`;
  return `₹${Math.round(v)}`;
};

const fmtPct = (n) => `${(Number(n) || 0).toFixed(1)}%`;

const healthPill = (pct) => {
  if (pct >= 75) return { bg: "bg-emerald-50", fg: "text-emerald-700", border: "border-emerald-200", label: "Healthy" };
  if (pct >= 40) return { bg: "bg-amber-50", fg: "text-amber-700", border: "border-amber-200", label: "At risk" };
  return { bg: "bg-red-50", fg: "text-red-700", border: "border-red-200", label: "Critical" };
};

// ---------- small components ----------

function KpiCard({ icon: Icon, label, value, sub, trend, accent = "slate", to, testid, hint }) {
  const accents = {
    slate: "bg-slate-100 text-slate-700",
    blue: "bg-blue-50 text-blue-900",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    indigo: "bg-indigo-50 text-indigo-700",
  };
  const TrendIcon = trend?.dir === "up" ? TrendUp : TrendDown;
  const trendColor = trend?.dir === "up" ? "text-emerald-600" : "text-red-600";

  const inner = (
    <div
      data-testid={testid}
      className="group p-5 border border-slate-200 bg-white rounded-lg flex flex-col gap-3 transition-all hover:border-slate-300 hover:shadow-sm cursor-pointer"
    >
      <div className="flex items-start justify-between">
        <div className={`h-9 w-9 rounded-md flex items-center justify-center ${accents[accent]}`}>
          <Icon size={18} weight="duotone" />
        </div>
        {trend && trend.delta !== null && (
          <div className={`flex items-center gap-1 text-xs font-medium ${trendColor}`}>
            <TrendIcon size={12} weight="bold" />
            <span>{trend.label}</span>
          </div>
        )}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-1">
          <span>{label}</span>
          {hint && (
            <span
              title={hint}
              className="cursor-help text-slate-400 hover:text-slate-600 normal-case tracking-normal"
              data-testid={testid ? `${testid}-hint` : undefined}
            >
              ⓘ
            </span>
          )}
        </div>
        <div className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">{sub}</div>}
      </div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function AlertRow({ alert }) {
  const tone = {
    critical: { bg: "bg-red-50", border: "border-red-200", fg: "text-red-700", icon: Warning },
    warn: { bg: "bg-amber-50", border: "border-amber-200", fg: "text-amber-700", icon: Lightning },
    info: { bg: "bg-blue-50", border: "border-blue-200", fg: "text-blue-700", icon: Info },
  }[alert.severity] || {
    bg: "bg-slate-50", border: "border-slate-200", fg: "text-slate-700", icon: Info,
  };
  const Icon = tone.icon;
  return (
    <div
      className={`flex items-center justify-between gap-3 px-4 py-3 border ${tone.border} ${tone.bg} rounded-md`}
      data-testid={`cockpit-alert-${alert.key}`}
    >
      <div className="flex items-start gap-3 min-w-0">
        <div className={`mt-0.5 ${tone.fg}`}>
          <Icon size={18} weight="duotone" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">{alert.title}</div>
          {alert.subtitle && <div className="text-xs text-slate-600 mt-0.5 truncate">{alert.subtitle}</div>}
        </div>
      </div>
      {alert.action_href && (
        <Link
          to={alert.action_href}
          data-testid={`cockpit-alert-link-${alert.key}`}
          className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-white border border-slate-200 hover:border-slate-300 rounded-md text-slate-900 transition-colors"
        >
          {alert.action_label || "View"} <ArrowRight size={12} />
        </Link>
      )}
    </div>
  );
}

function HealthPill({ pct }) {
  const p = healthPill(pct);
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded ${p.bg} ${p.fg} border ${p.border}`}>
      {fmtPct(pct)}
    </span>
  );
}

// ---------- Trend chart ----------

function TrendChart({ data, themeColor = "#1E3A8A" }) {
  if (!data || data.length === 0) {
    return (
      <div className="h-56 flex flex-col items-center justify-center text-slate-400">
        <Calendar size={28} className="mb-1" />
        <div className="text-sm">No collections in the last 30 days</div>
      </div>
    );
  }
  return (
    <div className="h-56" data-testid="cockpit-trend-chart">
      <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
        <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={themeColor} stopOpacity={0.32} />
              <stop offset="95%" stopColor={themeColor} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
          <XAxis
            dataKey="bucket"
            tick={{ fontSize: 10 }}
            stroke="#94A3B8"
            tickFormatter={(v) => v?.slice(5)} // MM-DD
            interval="preserveStartEnd"
            minTickGap={20}
          />
          <YAxis
            tick={{ fontSize: 10 }}
            stroke="#94A3B8"
            tickFormatter={fmtShortINR}
            width={56}
          />
          <Tooltip
            formatter={(v) => fmtINR(v)}
            labelFormatter={(l) => `Date: ${l}`}
            contentStyle={{ borderRadius: 6, fontSize: 12, border: "1px solid #E2E8F0" }}
          />
          <Area
            type="monotone"
            dataKey="amount"
            stroke={themeColor}
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#trendGrad)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------- Mode mix donut ----------

const MODE_COLORS = {
  cash: "#10B981",
  upi: "#1E3A8A",
  bank: "#8B5CF6",
  other: "#94A3B8",
};

function ModeMixDonut({ mix }) {
  const data = Object.entries(mix || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: k.toUpperCase(), value: v, key: k }));
  const total = data.reduce((s, d) => s + d.value, 0);

  if (total === 0) {
    return (
      <div className="h-40 flex flex-col items-center justify-center text-slate-400">
        <Receipt size={24} className="mb-1" />
        <div className="text-xs">No receipts today</div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3" data-testid="cockpit-mode-mix">
      <div className="h-32 w-32 shrink-0">
        <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={36}
              outerRadius={56}
              dataKey="value"
              paddingAngle={2}
            >
              {data.map((d) => (
                <Cell key={d.key} fill={MODE_COLORS[d.key] || "#94A3B8"} />
              ))}
            </Pie>
            <Tooltip formatter={(v) => fmtINR(v)} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex-1 space-y-1.5 text-xs">
        {data.map((d) => (
          <div key={d.key} className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="h-2.5 w-2.5 rounded-sm shrink-0"
                style={{ backgroundColor: MODE_COLORS[d.key] || "#94A3B8" }}
              />
              <span className="text-slate-700 font-medium truncate">{d.name}</span>
            </div>
            <span className="text-slate-900 font-mono tabular-nums">
              {Math.round((d.value / total) * 100)}%
            </span>
          </div>
        ))}
        <div className="pt-1.5 mt-1.5 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
          <span>Total</span>
          <span className="text-slate-900 font-semibold">{fmtINR(total)}</span>
        </div>
      </div>
    </div>
  );
}

// ---------- Empty state ----------

function EmptyOnboarding({ school }) {
  return (
    <div className="p-8 border border-dashed border-slate-300 rounded-lg bg-slate-50/50 text-center" data-testid="cockpit-empty">
      <Calendar size={36} weight="duotone" className="mx-auto mb-3 text-amber-600" />
      <h3 className="text-lg font-bold text-slate-900 font-heading">No active academic session</h3>
      <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
        Set up your first academic session to start tracking collections, classes, and students.
      </p>
      <Link
        to="/sessions"
        data-testid="cockpit-setup-session"
        className="inline-flex items-center gap-2 mt-4 px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-md transition-colors"
      >
        Set up session <ArrowRight size={14} />
      </Link>
    </div>
  );
}

// ---------- Main ----------

export default function SchoolAdminDashboard() {
  const { user } = useAuth();
  const [school, setSchool] = useState(null);
  const [cockpit, setCockpit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get("/school/me"),
          api.get("/school/dashboard/cockpit"),
        ]);
        setSchool(s.data);
        setCockpit(c.data);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const themeColor = school?.theme_color || "#1E3A8A";
  const hasActiveSession = !!cockpit?.session;

  return (
    <div className="space-y-6" data-testid="schooladmin-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-4">
          {school && (
            <div
              className="h-14 w-14 rounded-md flex items-center justify-center text-white text-xl font-bold font-heading"
              style={{ backgroundColor: themeColor }}
            >
              {school.school_name.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
              Management cockpit
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">
              {school?.school_name || "Your school"}
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              {hasActiveSession ? (
                <>
                  Session <span className="font-semibold text-slate-900">{cockpit.session.name}</span> · Welcome back, {user?.name?.split(" ")[0]}.
                </>
              ) : (
                <>Welcome back, {user?.name?.split(" ")[0]}.</>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/reports"
            data-testid="cockpit-reports-link"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-slate-200 hover:bg-slate-50 rounded-md text-slate-900 transition-colors"
          >
            Reports <ArrowRight size={12} />
          </Link>
          <Link
            to="/branding"
            data-testid="cockpit-branding-link"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-slate-200 hover:bg-slate-50 rounded-md text-slate-900 transition-colors"
          >
            Branding
          </Link>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{error}</div>
      )}

      {/* Empty / onboarding state */}
      {!loading && !hasActiveSession && <EmptyOnboarding school={school} />}

      {/* KPI trio: Today / MTD / STD + Phase 29 Fee-month card (school_admin only) */}
      {hasActiveSession && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="cockpit-kpis">
          <KpiCard
            icon={CurrencyInr}
            label="Today (school-wide)"
            hint="RAW receipts received today across all staff. Each cashier's own Operations Dashboard shows only their share."
            value={fmtINR(cockpit?.today?.amount ?? 0)}
            sub={
              cockpit?.today?.receipts
                ? `${cockpit.today.receipts} receipt${cockpit.today.receipts === 1 ? "" : "s"}`
                : "No receipts yet today"
            }
            accent="emerald"
            to="/payments"
            testid="cockpit-kpi-today"
          />
          <KpiCard
            icon={TrendUp}
            label="Receipts dated this month"
            hint="RAW receipts received in this calendar month (by receipt date). Different from '{Month} fee collection' which buckets by the FEE MONTH the receipt is tagged against."
            value={fmtINR(cockpit?.mtd?.amount ?? 0)}
            sub={
              cockpit?.mtd?.amount > 0
                ? "Total cash inflow this calendar month"
                : "No collections yet this month"
            }
            trend={
              cockpit?.mtd?.vs_prev_pct_delta !== null && cockpit?.mtd?.vs_prev_pct_delta !== undefined
                ? {
                    dir: cockpit.mtd.vs_prev_pct_delta >= 0 ? "up" : "down",
                    delta: cockpit.mtd.vs_prev_pct_delta,
                    label: `${cockpit.mtd.vs_prev_pct_delta >= 0 ? "+" : ""}${cockpit.mtd.vs_prev_pct_delta}% vs prev`,
                  }
                : null
            }
            accent="blue"
            to="/reports"
            testid="cockpit-kpi-mtd"
          />
          {/* Phase 29 — academic-month fee collection. Only rendered when
              the backend (which is school_admin gated) returns the new
              fee_month block. Cashier/teacher never call this endpoint
              and never receive this field. */}
          {cockpit?.fee_month && (
            <KpiCard
              icon={TrendUp}
              label={`${cockpit.fee_month.month_label || ""} fee collection`}
              value={fmtINR(cockpit.fee_month.amount ?? 0)}
              sub={
                cockpit.fee_month.expected > 0
                  ? `${fmtPct(cockpit.fee_month.pct)} of expected · ${fmtINR(cockpit.fee_month.expected)} target`
                  : "Set fee structures to see target"
              }
              accent="violet"
              to="/reports/monthly-verification"
              testid="cockpit-kpi-fee-month"
            />
          )}
          <KpiCard
            icon={Hourglass}
            label="Session collected"
            hint="CAPPED: money received that maps to genuine session liability. Excludes over-collection. Used by Reports KPI 'Collected (in range)' too. The 'Receipts dated this month' card above is RAW (all money) — they may differ when over-collection exists."
            value={fmtINR(cockpit?.session_to_date?.amount ?? 0)}
            sub={
              cockpit?.session_to_date?.expected > 0
                ? (cockpit?.session_to_date?.over_collection > 0
                    ? `${fmtPct(cockpit.session_to_date.pct)} of expected · ${fmtINR(cockpit.session_to_date.over_collection)} over-collection`
                    : `${fmtPct(cockpit.session_to_date.pct)} of expected · ${fmtINR(cockpit.session_to_date.pending)} pending`)
                : "Add students + fee structures to track"
            }
            accent="indigo"
            to="/reports"
            testid="cockpit-kpi-std"
          />
        </div>
      )}

      {/* Trend chart */}
      {hasActiveSession && (
        <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="cockpit-trend">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
                Collection trend
              </div>
              <div className="text-sm text-slate-700 mt-0.5">Daily collection · last 30 days</div>
            </div>
            <Link
              to="/reports"
              data-testid="cockpit-trend-deeplink"
              className="text-xs font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            >
              Open Reports <ArrowRight size={12} />
            </Link>
          </div>
          <TrendChart data={cockpit?.trend_30d} themeColor={themeColor} />
        </div>
      )}

      {/* Alerts strip */}
      {hasActiveSession && cockpit?.alerts && cockpit.alerts.length > 0 && (
        <div className="space-y-2" data-testid="cockpit-alerts">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 px-1">
            Alerts &amp; actions
          </div>
          <div className="space-y-2">
            {cockpit.alerts.map((a) => (
              <AlertRow key={a.key} alert={a} />
            ))}
          </div>
        </div>
      )}

      {/* No-alerts confidence pill (only when active session + no alerts) */}
      {hasActiveSession && cockpit?.alerts && cockpit.alerts.length === 0 && (
        <div
          className="flex items-center gap-3 px-4 py-3 border border-emerald-200 bg-emerald-50 rounded-md"
          data-testid="cockpit-no-alerts"
        >
          <CheckCircle size={18} weight="duotone" className="text-emerald-600" />
          <div className="text-sm text-emerald-900">
            <span className="font-semibold">All clear.</span> No outstanding alerts right now.
          </div>
        </div>
      )}

      {/* Bottom 3-column: top pending classes / today's collectors / mode mix */}
      {hasActiveSession && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Top pending classes */}
          <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="cockpit-top-pending">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
              Top pending classes
            </div>
            <div className="text-sm text-slate-700 mt-0.5">By pending amount</div>
            {cockpit.top_pending_classes.length === 0 ? (
              <div className="mt-4 py-6 text-center text-xs text-slate-400">
                <CheckCircle size={20} className="mx-auto mb-1 text-emerald-500" />
                All classes fully collected.
              </div>
            ) : (
              <ul className="mt-3 space-y-2">
                {cockpit.top_pending_classes.map((c, idx) => (
                  <li
                    key={c.class_id}
                    data-testid={`cockpit-pending-class-${c.class_id}`}
                    className="flex items-center justify-between gap-2 px-3 py-2 border border-slate-100 rounded-md hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="text-xs font-mono text-slate-400 w-4">{idx + 1}</span>
                      <span className="text-sm font-semibold text-slate-900 truncate">{c.class_name}</span>
                      <HealthPill pct={c.pct} />
                    </div>
                    <span className="text-xs font-mono tabular-nums text-slate-700 shrink-0">
                      {fmtShortINR(c.pending)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <Link
              to="/defaulters"
              data-testid="cockpit-defaulters-link"
              className="block mt-3 pt-3 border-t border-slate-100 text-xs font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            >
              Open defaulters <ArrowRight size={12} />
            </Link>
          </div>

          {/* Today's collectors */}
          <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="cockpit-today-collectors">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
              Today&apos;s collectors
            </div>
            <div className="text-sm text-slate-700 mt-0.5">Who collected what</div>
            {cockpit.today_collectors.length === 0 ? (
              <div className="mt-4 py-6 text-center text-xs text-slate-400">
                <ChalkboardTeacher size={20} className="mx-auto mb-1" />
                No collections yet today.
              </div>
            ) : (
              <ul className="mt-3 space-y-2">
                {cockpit.today_collectors.map((c) => (
                  <li
                    key={c.collector_id || c.collector_name}
                    className="flex items-center justify-between gap-2 px-3 py-2 border border-slate-100 rounded-md"
                    data-testid={`cockpit-collector-${c.collector_id || "anon"}`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="h-7 w-7 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[11px] font-semibold shrink-0">
                        {(c.collector_name || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">{c.collector_name}</div>
                        <div className="text-[11px] text-slate-500">
                          {c.count} receipt{c.count === 1 ? "" : "s"}
                        </div>
                      </div>
                    </div>
                    <span className="text-sm font-mono tabular-nums text-slate-900 shrink-0">
                      {fmtShortINR(c.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Mode mix */}
          <div className="border border-slate-200 bg-white rounded-lg p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
              Today · mode mix
            </div>
            <div className="text-sm text-slate-700 mt-0.5">Cash / UPI / Bank breakdown</div>
            <div className="mt-3">
              <ModeMixDonut mix={cockpit.today.mode_mix} />
            </div>
            {cockpit.cash_pending_banking?.amount > 0 && (
              <Link
                to="/payments?mode=cash"
                data-testid="cockpit-cash-banking-link"
                className="mt-3 flex items-center gap-2 px-3 py-2 border border-amber-200 bg-amber-50 rounded-md text-xs text-amber-800 hover:bg-amber-100"
              >
                <Bank size={14} weight="duotone" />
                <span className="font-semibold">{fmtINR(cockpit.cash_pending_banking.amount)}</span>
                <span>cash collected · last 7 days</span>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Quick actions footer (always visible) */}
      <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="cockpit-quick-actions">
        <h3 className="text-base font-bold text-slate-900 font-heading">Quick actions</h3>
        <p className="text-xs text-slate-500 mt-1">Common operations</p>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Link
            to="/students"
            data-testid="quick-add-student"
            className="flex items-center gap-2 px-3 py-2.5 border border-slate-200 hover:bg-slate-50 rounded-md text-sm font-medium text-slate-900"
          >
            <Plus size={14} /> Add student
          </Link>
          <Link
            to="/classes"
            data-testid="quick-add-class"
            className="flex items-center gap-2 px-3 py-2.5 border border-slate-200 hover:bg-slate-50 rounded-md text-sm font-medium text-slate-900"
          >
            <Plus size={14} /> Create class
          </Link>
          <Link
            to="/teachers"
            data-testid="quick-add-teacher"
            className="flex items-center gap-2 px-3 py-2.5 border border-slate-200 hover:bg-slate-50 rounded-md text-sm font-medium text-slate-900"
          >
            <Plus size={14} /> Onboard teacher
          </Link>
          <Link
            to="/fees-structure"
            data-testid="quick-fee-structure"
            className="flex items-center gap-2 px-3 py-2.5 border border-slate-200 hover:bg-slate-50 rounded-md text-sm font-medium text-slate-900"
          >
            <Plus size={14} /> Fee structure
          </Link>
        </div>
      </div>

      {loading && (
        <div className="text-center text-slate-400 text-sm" data-testid="cockpit-loading">
          Loading cockpit…
        </div>
      )}
    </div>
  );
}
